"use client";
import * as React from "react";
import { useState, useEffect, useRef } from "react";
import {
  Box,
  Flex,
  Text,
  Input,
  IconButton,
  Image,
  CloseButton,
  Spinner,
} from "@chakra-ui/react";
import { chatService, ChatMessage, ChatbotResponseData } from "../../services/chatService";
import { v4 as uuidv4 } from 'uuid';

interface ChatInterfaceProps {
  onClose: () => void;
  businessIdentifier: string;
}

// User message component
const UserMessage = ({ content }: { content: string }) => (
  <Box
    alignSelf="flex-end"
    px={4}
    py={4}
    bg="purple.400"
    maxW="80%"
    borderRadius="xl"
    role="log"
    aria-label="User message"
  >
    {content}
  </Box>
);

// Assistant message component
const AssistantMessage = ({ content, isLoading }: { content: React.ReactNode; isLoading?: boolean }) => (
  <Flex gap={1} alignItems="flex-end" width="100%" lineHeight="5" color="gray.800">
    <Image
      src="https://cdn.builder.io/api/v1/image/assets/TEMP/c66db7376ff0bffa15dc3d537cff32844df8cc94?placeholderIfAbsent=true&apiKey=07628b7bd3c04a709b3d6c83bfffc792"
      objectFit="contain"
      borderRadius="29px"
      width="30px"
      height="30px"
      alt="Assistant avatar"
    />
    <Box
      alignSelf="flex-start"
      px={4}
      py={4}
      bg="white"
      borderRadius="xl"
      maxW="80%"
      role="log"
      aria-label="Assistant message"
    >
      {isLoading ? <Spinner size="sm" /> : content}
    </Box>
  </Flex>
);

const ChatInterface: React.FC<ChatInterfaceProps> = ({ onClose, businessIdentifier }) => {
  const [inputValue, setInputValue] = useState("");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | undefined>(undefined);
  const messagesEndRef = useRef<null | HTMLDivElement>(null);
  const isLoadingFromStorage = useRef(true);
  const previousBusinessId = useRef<string>(businessIdentifier);

  // Load messages and sessionId from localStorage on mount and when businessIdentifier changes
  useEffect(() => {
    const storageKey = `chat_messages_${businessIdentifier}`;
    const sessionKey = `chat_session_${businessIdentifier}`;
    
    // Check if businessIdentifier changed
    const businessIdChanged = previousBusinessId.current !== businessIdentifier;
    if (businessIdChanged) {
      previousBusinessId.current = businessIdentifier;
      isLoadingFromStorage.current = true;
    }
    
    try {
      const savedMessages = localStorage.getItem(storageKey);
      if (savedMessages) {
        const parsedMessages = JSON.parse(savedMessages);
        const restoredMessages: ChatMessage[] = parsedMessages.map((msg: any) => ({
          ...msg,
          content: typeof msg.content === 'string' ? msg.content : String(msg.content || ''),
        }));
        setMessages(restoredMessages);
      } else {
        setMessages([]);
      }
      
      const savedSessionId = localStorage.getItem(sessionKey);
      if (savedSessionId) {
        setSessionId(savedSessionId);
      } else {
        setSessionId(undefined);
      }
    } catch (error) {
      console.error("Error loading messages from localStorage:", error);
    }
    
    // Mark loading as complete - use a microtask to ensure this runs after state updates
    Promise.resolve().then(() => {
      isLoadingFromStorage.current = false;
    });
  }, [businessIdentifier]);

  // Save messages to localStorage whenever they change (but not while loading from storage)
  useEffect(() => {
    // Skip saving if we're currently loading from storage
    if (isLoadingFromStorage.current) {
      return;
    }

    const storageKey = `chat_messages_${businessIdentifier}`;
    try {
      // Only save messages that are not loading
      const messagesToSave = messages.filter(msg => !msg.isLoading);
      // Convert content to string for storage
      const messagesForStorage = messagesToSave.map(msg => ({
        ...msg,
        content: typeof msg.content === 'string' ? msg.content : String(msg.content || ''),
      }));
      
      // Save to localStorage
      localStorage.setItem(storageKey, JSON.stringify(messagesForStorage));
    } catch (error) {
      console.error("Error saving messages to localStorage:", error);
    }
  }, [messages, businessIdentifier]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleSendMessage = async () => {
    if (inputValue.trim() && businessIdentifier) {
      const userMessage: ChatMessage = {
        id: uuidv4(),
        type: "user",
        content: inputValue.trim(),
      };
      setMessages((prevMessages) => [...prevMessages, userMessage]);
      setInputValue("");
      setIsLoading(true);

      try {
        const response = await chatService.askChatbot({
          question: userMessage.content as string,
          business_identifier: businessIdentifier,
          session_id: sessionId,
        });

        // Store session_id from response for next request
        if (response.session_id) {
          setSessionId(response.session_id);
          // Save sessionId to localStorage
          try {
            const sessionKey = `chat_session_${businessIdentifier}`;
            localStorage.setItem(sessionKey, response.session_id);
          } catch (error) {
            console.error("Error saving sessionId to localStorage:", error);
          }
        }

        const assistantMessage: ChatMessage = {
          id: uuidv4(),
          type: "assistant",
          content: response.answer || "Sorry, I couldn't get a response.",
        };
        setMessages((prevMessages) => [...prevMessages, assistantMessage]);
      } catch (error) {
        console.error("Chatbot API error:", error);
        const errorMessage: ChatMessage = {
          id: uuidv4(),
          type: "assistant",
          content: "Sorry, something went wrong. Please try again.",
        };
        setMessages((prevMessages) => [...prevMessages, errorMessage]);
      }
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && inputValue.trim()) {
      handleSendMessage();
    }
  };

  const handleNewChat = () => {
    const storageKey = `chat_messages_${businessIdentifier}`;
    const sessionKey = `chat_session_${businessIdentifier}`;
    
    // Clear messages and sessionId from state
    setMessages([]);
    setSessionId(undefined);
    
    // Clear from localStorage
    try {
      localStorage.removeItem(storageKey);
      localStorage.removeItem(sessionKey);
    } catch (error) {
      console.error("Error clearing chat data from localStorage:", error);
    }
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      p={4}
      fontSize="xs"
      fontWeight="medium"
      borderWidth="2px"
      borderStyle="solid"
      bg="gray.100"
      borderColor="gray.400"
      maxW="412px"
      borderRadius="35px"
      boxShadow="xl"
      width="100%"
    >
      <Flex justifyContent="space-between" alignItems="center" mb={2}>
        <Text fontSize="sm" fontWeight="semibold" color="gray.800">
          Chat Assistant
        </Text>
        <Flex gap={2} alignItems="center">
          <IconButton
            aria-label="New Chat"
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 5v14M5 12h14" />
              </svg>
            }
            size="sm"
            variant="ghost"
            colorScheme="gray"
            onClick={handleNewChat}
            title="New Chat"
          />
          <CloseButton onClick={onClose} color="gray.500" size="sm" />
        </Flex>
      </Flex>

      {messages.length < 1 && (
        <Box
          display="flex"
          overflow="hidden"
          flexDirection="column"
          justifyContent="center"
          p={4}
          width="100%"
          lineHeight="loose"
          borderRadius="xl"
          bg="gray.100"
          minH="48px"
          boxShadow="0px 0px 7px rgba(0,0,0,0.25)"
          color="gray.800"
          role="region"
          aria-label="Welcome message"
        >
          <Flex gap={2} justifyContent="center" alignItems="center" width="100%">
            <Image
              src="https://cdn.builder.io/api/v1/image/assets/TEMP/a076cc62a6efdaa1f0c3c2443b764c9b2c454694?placeholderIfAbsent=true&apiKey=07628b7bd3c04a709b3d6c83bfffc792"
              objectFit="contain"
              alignSelf="stretch"
              my="auto"
              width="16px"
              height="16px"
              alt="Chat icon"
            />
            <Text alignSelf="stretch" my="auto" opacity={0.7} color="gray.800">
              Hi, you can ask me anything about your project
            </Text>
          </Flex>
        </Box>
      )}

      <Flex 
        flexDirection="column" 
        alignItems="stretch" 
        alignSelf="center" 
        pb={2} 
        mt={2.5} 
        width="100%" 
        color="white" 
        gap={6}
        overflowY="auto"
        maxHeight="300px"
        sx={{
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          '-ms-overflow-style': 'none',
          'scrollbar-width': 'none',
        }}
      >
        {messages.map((msg) =>
          msg.type === "user" ? (
            <UserMessage key={msg.id} content={msg.content as string} />
          ) : (
            <AssistantMessage key={msg.id} content={msg.content} isLoading={msg.isLoading} />
          )
        )}
        {isLoading && messages[messages.length -1]?.type === 'user' && (
          <AssistantMessage key="loading" content="" isLoading={true} />
        )}
        <div ref={messagesEndRef} />
      </Flex>

      {/* Input area */}
      <Flex gap={2.5} alignItems="center" mt={2.5} width="100%" color="gray.400">
        <Flex
          overflow="hidden"
          flex="1"
          gap={2.5}
          alignItems="center"
          alignSelf="stretch"
          px={4}
          py={3}
          my="auto"
          bg="white"
          boxShadow="sm"
          borderRadius="full"
        >
          <Input
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="What are my major lead generators?"
            border="none"
            _focus={{ boxShadow: "none" }}
            color="gray.600"
            flex="1"
            alignSelf="stretch"
          />
          {inputValue && (
            <IconButton
              aria-label="Clear input"
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              }
              size="sm"
              variant="ghost"
              colorScheme="gray"
              onClick={() => setInputValue("")}
            />
          )}
          {!inputValue && (
            <Image
              src="https://cdn.builder.io/api/v1/image/assets/TEMP/d184e9269edaa0b4e7937e415174be4341470e1f?placeholderIfAbsent=true&apiKey=07628b7bd3c04a709b3d6c83bfffc792"
              objectFit="contain"
              alignSelf="stretch"
              my="auto"
              width="24px"
              height="24px"
              alt="Input icon"
            />
          )}
        </Flex>
        
        <IconButton
          aria-label="Send message"
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" transform="rotate(90 13 13)" />
            </svg>
          }
          size="lg"
          isRound
          colorScheme="purple"
          bg={inputValue.trim() ? "purple.500" : "purple.300"}
          _hover={{ bg: inputValue.trim() ? "purple.600" : "purple.300" }}
          color="white"
          isDisabled={!inputValue.trim()}
          onClick={handleSendMessage}
          width="54px"
          height="54px"
        />
      </Flex>
    </Box>
  );
};

export default ChatInterface; 